import { useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { useEffect } from 'react'

import { CONTROLS } from '@brickup/shared/locales/de/controls'
import { MATCHING } from '@brickup/shared/locales/de/matching'
import { NAV } from '@brickup/shared/locales/de/nav'
import { useRobotStatus, useRunRobot } from '@/api/matching'
import { useMatchesForMandate, useSearchMandate } from '@/api/search-mandates'
import { Button } from '@/components/base'
import {
  listSearchValidator,
  useListFilters,
} from '@/components/Internal/DataTable'
import { MasterDetailLayout } from '@/components/Internal/MasterDetailLayout'
import { mandateListFilters } from '@/components/Internal/Matching/mandate-list-filters'
import { MatchesSidebar } from '@/components/Internal/Matching/MatchesSidebar'
import { RobotControl } from '@/components/Internal/Matching/RobotControl'
import { SearchOffersTable } from '@/components/Internal/Matching/SearchOffersTable'
import { PageLayout } from '@/components/Internal/PageLayout'
import { useRobotWebSocketStore } from '@/stores/use-robot-websocket-store'

export const Route = createFileRoute('/internal/matching')({
  validateSearch: listSearchValidator(mandateListFilters),
  component: MatchingScreen,
})

/**
 * Search mandates on the left, one mandate's matches on the right.
 *
 * Which mandate is open lives in the URL, so a property's detail pane can link
 * straight at the customer it was matched to.
 */
function MatchingScreen() {
  const filters = useListFilters(mandateListFilters, Route)
  const { selected, matchStatus } = filters.values

  const runRobot = useRunRobot()
  const isRunning = useRobotStatus().data?.running ?? false

  // The robot reports its progress over a socket rather than by polling, and
  // this screen is the only one that watches it.
  const queryClient = useQueryClient()
  useEffect(
    () => useRobotWebSocketStore.getState().connect(queryClient),
    [queryClient],
  )

  const { data: selectedOffer = null } = useSearchMandate(selected || null)

  const {
    items: matches,
    isLoading: isLoadingMatches,
    fetchNextPage,
    hasMore,
    isLoadingMore,
  } = useMatchesForMandate(selected || null, matchStatus)

  return (
    <PageLayout title={NAV.matching}>
      <MasterDetailLayout
        storageKey="split:matching"
        sidebar={
          selectedOffer ? (
            <MatchesSidebar
              matches={matches}
              selectedOffer={selectedOffer}
              matchStatus={matchStatus}
              onMatchStatusChange={status => filters.set('matchStatus', status)}
              isLoadingMatches={isLoadingMatches}
              onClose={() => filters.set('selected', '')}
              onLoadMore={fetchNextPage}
              hasMore={hasMore}
              isLoadingMore={isLoadingMore}
            />
          ) : undefined
        }
      >
        <RobotControl />
        <SearchOffersTable
          filters={filters}
          actions={
            <Button
              size="sm"
              isLoading={runRobot.isPending}
              disabled={isRunning}
              onClick={() => runRobot.mutate(undefined)}
            >
              {runRobot.isPending
                ? CONTROLS.cronJob.running
                : isRunning
                  ? MATCHING.robot.alreadyRunning
                  : MATCHING.robot.checkAll}
            </Button>
          }
        />
      </MasterDetailLayout>
    </PageLayout>
  )
}
